import { useMemo, useState } from 'react'
import type { ProjectSummary } from '../lib/db'
import { setProjectStatus } from '../lib/db'
import { money } from '../lib/paper'
import type { JobStatus } from '../types/document'
import {
  age,
  ageWording,
  byAttention,
  DOC_STATES,
  docState,
  moves,
  hasDocument,
  NOT_TRACKED,
  nextAction,
  outstanding,
  waitingCount,
  type DocRef,
  type DocState,
} from '../lib/estimates'
import { Empty, Loading, NotKnown } from './bits'
import { IconChevron, IconMore } from './icons'

/**
 * EVERY QUOTE YOU HAVE OUT, AND WHO HAS NOT ANSWERED.
 *
 * PROP-01. This existed as a filter on the job list, which answered the wrong
 * question: the job list is sorted by what you touched last, and the thing a
 * contractor needs in November is what he has NOT touched. So this is the same
 * records, sorted by how long they have been sitting, longest first, and the
 * top of the list is who to call.
 *
 * NO SECOND FINANCIAL RECORD. The spec is explicit that an estimate and a
 * proposal are presentation options over one shared scope. Nothing is stored
 * here. The number, the date and the total are read off the job the same way
 * the job page reads them, so the two can never disagree.
 *
 * NO FIGURE CARDS, per the list pages. One sentence at the top that answers
 * the question the screen is opened with, and then the rows. Three tiles
 * restating what is underneath them is furniture.
 */
export default function Estimates({
  items,
  onChanged,
  onOpenJob,
}: {
  items: ProjectSummary[] | null
  onChanged: () => void
  onOpenJob: (job: ProjectSummary) => void
}) {
  const [filter, setFilter] = useState<DocState | 'all'>('all')
  const [openId, setOpenId] = useState<string | null>(null)
  const today = new Date().toISOString().slice(0, 10)

  const jobs = useMemo(() => items ?? [], [items])

  /* Only the priced ones. Photographing a house is not quoting it. */
  const docs = useMemo<ProjectSummary[]>(() => jobs.filter((j) => hasDocument(j)), [jobs])

  const rows = useMemo(() => {
    const sorted = byAttention(docs as DocRef[], today) as ProjectSummary[]
    return filter === 'all' ? sorted : sorted.filter((j) => docState(j) === filter)
  }, [docs, filter, today])

  const out = outstanding(docs as DocRef[])
  const waiting = waitingCount(docs as DocRef[])
  const longest = useMemo(() => {
    const ages = docs
      .filter((j) => docState(j) === 'waiting')
      .map((j) => age(j, today))
      .filter((n): n is number => n !== null)
    return ages.length ? Math.max(...ages) : null
  }, [docs, today])

  const who = (j: ProjectSummary) => j.customer.trim() || j.address.trim() || j.name

  /* Named outcomes, in one place, shared with the job's own page. */
  const movesFor = (j: ProjectSummary) => moves(docState(j))


  const move = async (id: string, to: JobStatus) => {
    await setProjectStatus(id, to)
    setOpenId(null)
    onChanged()
  }

  if (items === null) return <Loading what="Reading your jobs" />

  return (
    <>
      <div className="ws-head">
        <div>
          <h1>Estimates</h1>
          {/* Says what the order actually is. It is not purely by age: a quote
              lost a month ago is older than a live one and belongs below it. */}
          <p>
            Every quote you have out. The ones still waiting come first, longest at the top, so
            the head of this list is who to call.
          </p>
        </div>
      </div>

      {/*
        THE ANSWER, IN ONE SENTENCE. The question this screen is opened with is
        how much is out there and how long has it been, and that is two numbers
        and a length of time, not three tiles.
      */}
      {docs.length > 0 && (
        <p className="est-answer">
          {waiting === 0 ? (
            <>Nothing is waiting on an answer right now.</>
          ) : (
            <>
              <b>{money(out)}</b> is out on {waiting} {waiting === 1 ? 'quote' : 'quotes'}.
              {longest !== null && longest > 0 && (
                <> The oldest has been waiting {ageWording(longest).toLowerCase()}.</>
              )}
            </>
          )}
        </p>
      )}

      <div className="ws-tools">
        <div className="ws-tabs">
          <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </button>
          {DOC_STATES.map((s) => (
            <button key={s.id} aria-pressed={filter === s.id} onClick={() => setFilter(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="ws-card">
          {docs.length === 0 ? (
            <Empty title="No quotes out yet">
              A job turns up here the moment it has a price on it.
            </Empty>
          ) : (
            <Empty kind="filtered" action={{ label: 'Show every quote', onClick: () => setFilter('all') }}
>
              Nothing matches that.
            </Empty>
          )}
        </div>
      ) : (

        <div className="ws-card est-list">
          <div className="ws-thead" aria-hidden="true">
            <span />
            <span>Quote</span>
            <span>State</span>
            <span>Amount</span>
            <span />
          </div>
          {rows.map((j) => {
            const state = docState(j)
            return (
              <div key={j.id} className="ws-item">
                <div className="ws-row">
                  <button
                    className="ws-open"
                    onClick={() => onOpenJob(j)}
                    aria-label={`Open ${who(j)}`}
                  >
                    <span className="ws-thumb">{j.thumb ? <img src={j.thumb} alt="" /> : <i />}</span>
                    {/*
                      THE NUMBER SITS WITH THE NAME, not on the line below it,
                      because LP-1041 is how this document is referred to on
                      the phone call the row exists to prompt.
                    */}
                    <span className="ws-name">
                      <strong>
                        {who(j)}
                        {j.money?.number && <em className="est-no">{j.money.number}</em>}
                      </strong>
                      <span>{nextAction(j, today)}</span>
                    </span>
                    <span
                      className={`ws-pill ${state}`}
                      title={DOC_STATES.find((s) => s.id === state)?.label}
                    >
                      {DOC_STATES.find((s) => s.id === state)?.short}
                    </span>
                    <span className="ws-amount">
                      {money(j.money?.total ?? 0)}
                      {j.money?.kind === 'invoice' && j.money.balance > 0 && (
                        <em>{money(j.money.balance)} due</em>
                      )}
                    </span>
                  </button>
                  <button
                    className="ws-go"
                    aria-expanded={openId === j.id}
                    aria-label={`More for ${who(j)}`}
                    onClick={() => setOpenId(openId === j.id ? null : j.id)}
                  >
                    {openId === j.id ? <IconChevron size={16} /> : <IconMore size={16} />}
                  </button>
                </div>

                {openId === j.id && (
                  <div className="ws-rowacts">
                    {movesFor(j).map((m) => (
                      <button key={m.to} className="btn" onClick={() => void move(j.id, m.to)}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/*
        AND WHAT THIS LIST DOES NOT KNOW.

        PROP-01 asks for sent and viewed dates and an expiry. One of those is
        something a person marked here; the other two have nothing behind them
        at all. A Viewed column with a guess in it would be the most damaging
        thing on this screen, because it is the column somebody would decide
        not to phone a customer over.
      */}
      <NotKnown title="What this list does not know" items={NOT_TRACKED} />
    </>
  )
}
